import {
  faArrowCircleDown,
  faArrowCircleLeft,
  faArrowCircleRight,
  faArrowCircleUp,
  faEllipsisV,
  faGripVertical,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import toSentence from "modules/5_view_helpers/to_sentence";
import React, { Fragment, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { Col, Popover, PopoverBody, Row } from "reactstrap";

export const FORM_BUILDER_COL = "FORM_BUILDER_COL";

const DroppableCol = (props) => {
  const {
    openEdit,
    columnValues,
    name,
    column_types,
    disabled,
    canGoDown,
    canGoLeft,
    canGoRight,
    canGoUp,
    moveLeft,
    moveRight,
    moveDown,
    moveUp,
    custom_form,
    rowIndex,
    colIndex,
    moveCol,
    insertColAsRow,
    canDropColOnRow,
  } = props;

  const [popoverOpen, setPopoverOpen] = useState(false);
  const togglePopover = () => setPopoverOpen(!popoverOpen);

  const canMove = canGoDown || canGoLeft || canGoRight || canGoUp;

  const containerRef = React.useRef(null);

  // Moving was always allowed even on locked revisions (matches the old arrow controls),
  // so drag/drop is intentionally NOT gated on `disabled` — only editing is.
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: FORM_BUILDER_COL,
      item: {
        rowIndex,
        colIndex,
        desktop_width: columnValues?.desktop_width,
        uid: columnValues?.uid,
      },
      canDrag: canMove,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [canMove, rowIndex, colIndex, columnValues?.desktop_width, columnValues?.uid]
  );

  // Sortable-style: reorder live while hovering. The item's indexes are mutated after each
  // move (standard react-dnd pattern) so subsequent hovers compute against the new position.
  const [, drop] = useDrop(
    () => ({
      accept: FORM_BUILDER_COL,
      hover: (item, monitor) => {
        if (item.rowIndex === rowIndex && item.colIndex === colIndex) return;

        if (canDropColOnRow(item, rowIndex)) {
          const moved = moveCol(item.rowIndex, item.colIndex, rowIndex, colIndex);
          if (moved) {
            item.rowIndex = moved.rowIndex;
            item.colIndex = moved.colIndex;
          }
          return;
        }

        // Doesn't fit in this row — pull it out as its own row above/below based on cursor half
        const rect = containerRef.current?.getBoundingClientRect();
        const y = monitor.getClientOffset()?.y;
        if (!rect || y == null) return;
        const below = y > rect.top + rect.height / 2;
        const newRowIndex = insertColAsRow(item.rowIndex, item.colIndex, below ? rowIndex + 1 : rowIndex);
        if (newRowIndex != null) {
          item.rowIndex = newRowIndex;
          item.colIndex = 0;
        }
      },
      // Moves already happened during hover; a handled no-op stops parent drop targets
      drop: () => undefined,
    }),
    [rowIndex, colIndex, moveCol, insertColAsRow, canDropColOnRow]
  );

  let containerClass = "colContainer";
  if (isDragging) containerClass += " colContainer--dragging";

  return (
    <Fragment>
      <div
        className={containerClass}
        style={{ cursor: disabled ? "not-allowed" : "zoom-in" }}
        ref={(node) => {
          containerRef.current = node;
          preview(drop(node));
        }}
      >
        {canMove && (
          <div ref={drag} className="colDragHandle" title="Drag to reorder" onClick={(e) => e.stopPropagation()}>
            <FontAwesomeIcon icon={faGripVertical} />
          </div>
        )}
        {canMove && (
          <Fragment>
            <div className="colMoveMenu" id={_.snakeCase(name)}>
              <FontAwesomeIcon icon={faEllipsisV} style={{ cursor: "pointer", padding: "5px" }} />
            </div>
            <Popover isOpen={popoverOpen} trigger="legacy" toggle={togglePopover} target={_.snakeCase(name)}>
              <PopoverBody>
                <Row>
                  <Col md={{ offset: 4 }}>
                    {canGoUp && (
                      <FontAwesomeIcon
                        style={{ cursor: "pointer" }}
                        icon={faArrowCircleUp}
                        size="2x"
                        onClick={() => {
                          moveUp();
                          togglePopover();
                        }}
                      />
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {canGoLeft && (
                      <FontAwesomeIcon
                        style={{ cursor: "pointer" }}
                        icon={faArrowCircleLeft}
                        size="2x"
                        onClick={() => {
                          moveLeft();
                          togglePopover();
                        }}
                      />
                    )}
                    {canGoRight && (
                      <FontAwesomeIcon
                        style={{ cursor: "pointer" }}
                        icon={faArrowCircleRight}
                        size="2x"
                        onClick={() => {
                          moveRight();
                          togglePopover();
                        }}
                      />
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col md={{ offset: 4 }}>
                    {canGoDown && (
                      <FontAwesomeIcon
                        style={{ cursor: "pointer" }}
                        icon={faArrowCircleDown}
                        size="2x"
                        onClick={() => {
                          moveDown();
                          togglePopover();
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </PopoverBody>
            </Popover>
          </Fragment>
        )}
        <div
          onClick={() => {
            openEdit();
          }}
        >
          <ColumnRenderer column_types={column_types} custom_form={custom_form} {...columnValues} />
        </div>
      </div>
    </Fragment>
  );
};

export default DroppableCol;

// Memoized: previews render real inputs (ReactSelect etc), the most expensive part of the tree.
// final-form updates immutably, so only the edited col's values change reference — every other
// preview skips its re-render while you type.
const ColumnRenderer = React.memo((props) => {
  const {
    column_types,
    column_type,
    conditionally_visible,
    conditional_value,
    conditional_field,
    hold_point,
    hold_point_value,
    hold_point_bypassable,
    custom_form,
  } = props;

  const findColumn = column_types.find((c) => c.value == column_type);
  const SelectedFormComponent = findColumn?.formComponent;

  let conditional_value_text = conditional_value;
  if (_.isArray(conditional_value)) {
    conditional_value_text = toSentence(_.map(conditional_value, "label"), { twoWordsConnector: " or " });
  }
  if (_.isBoolean(conditional_value)) {
    conditional_value_text = conditional_value ? "true" : "false";
  }

  let hold_value_text = hold_point_value;
  if (_.isArray(hold_point_value)) {
    hold_value_text = toSentence(_.map(hold_point_value, "label"), { twoWordsConnector: " or " });
  }
  if (_.isBoolean(hold_point_value)) {
    hold_value_text = hold_point_value ? "true" : "false";
  }

  let ConditionLabel = conditionally_visible && (
    <i>
      Is visible when {conditional_field} is <b>{conditional_value_text}</b>
    </i>
  );
  let HoldLabel = hold_point && (
    <i className="text-danger">
      Hold point when value is <b>{hold_value_text}</b>
      {hold_point_bypassable ? " (bypassable with comment)" : ""}
    </i>
  );

  if (SelectedFormComponent) {
    return (
      <Fragment>
        <SelectedFormComponent preview {...props} />
        {ConditionLabel}
        {HoldLabel}
      </Fragment>
    );
  }
  return null;
});
